import { downloadRasterPdf, download, getShareUrl, createPrintUrl, getFittedPageLayout, getA4RasterCaptureScale, generateArtworkDataUrl, drawTextOnCanvas, drawSignatureTextOnA4Canvas } from './delegate';
import html2canvasModule from 'html2canvas';
import jsPDF from 'jspdf';
import * as ConfigUtils from '../../../utils/IConfigUtils';

// ── Mocki zewnętrznych bibliotek ───────────────────────────────────────────────

jest.mock('html2canvas', () => ({
    __esModule: true,
    default: jest.fn(),
}));

jest.mock('jspdf', () => ({
    __esModule: true,
    default: jest.fn(),
}));

const mockHtml2canvas = html2canvasModule as jest.Mock;
const MockJsPDF = jsPDF as unknown as jest.Mock;

const mockSave = jest.fn();
const mockAddImage = jest.fn();
const mockToDataURL = jest.fn(() => 'data:image/jpeg;base64,FAKEDATA');

const mockCanvas = {
    toDataURL: mockToDataURL,
    width: 1240,
    height: 1754,
};

let capturedPrintContainer: HTMLElement | null = null;

// ── Mock środowiska ────────────────────────────────────────────────────────────

beforeEach(() => {
    jest.clearAllMocks();
    capturedPrintContainer = null;

    // Konfiguracja mocków po clearAllMocks
    mockToDataURL.mockReturnValue('data:image/jpeg;base64,FAKEDATA');
    mockHtml2canvas.mockImplementation((element) => {
        capturedPrintContainer = element as HTMLElement;
        return Promise.resolve(mockCanvas);
    });
    MockJsPDF.mockImplementation(() => ({
        addImage: mockAddImage,
        save: mockSave,
        output: () => new Blob()
    }));

    // window.URL (dla mocków testujących blob PDF)
    if (typeof window.URL.createObjectURL === 'undefined') {
        window.URL.createObjectURL = jest.fn();
    }
    if (typeof window.URL.revokeObjectURL === 'undefined') {
        window.URL.revokeObjectURL = jest.fn();
    }

    // window.config wymagane przez loadConfig()
    (window as any).config = {
        printServiceUrl: 'https://pdf.a-soft.pl/print/print',
        selfUrlForPrintService: 'https://interactive.cruz-diez.com/',
        version: 'v1.0',
    };

    // window.location
    delete (window as any).location;
    window.location = { search: '', pathname: '/', origin: 'https://interactive.cruz-diez.com' } as any;

    // localStorage
    localStorage.clear();

    // Wyczyść DOM po każdym teście
    const existing = document.getElementById('artwork-workspace');
    if (existing) existing.remove();
    document.querySelectorAll('.footer--signature').forEach((element) => element.remove());
});

// ── Pomocnicze dane testowe ────────────────────────────────────────────────────

const makeParams = (overrides = {}) => ({
    colors: ['FF0000', '00FF00', '0000FF'],
    stripesWidth: 1,
    shapes: {
        shapes: [
            { name: 'circle', tagName: 'circle', rotate: 0 },
            { name: 'square', tagName: 'square', rotate: 45 },
            { name: 'hidden', tagName: 'hidden', rotate: 0 },
        ],
    },
    shapePositions: [
        { left: 10, top: 20 },
        { left: 30, top: 40 },
        { left: 50, top: 60 },
    ],
    shapeColors: ['000000', 'FFFFFF', 'AAAAAA'],
    ...overrides,
});

const appendWorkspace = (width = 620, height = 877) => {
    const div = document.createElement('div');
    div.id = 'artwork-workspace';
    Object.defineProperty(div, 'offsetWidth', { value: width, configurable: true });
    Object.defineProperty(div, 'offsetHeight', { value: height, configurable: true });
    document.body.appendChild(div);
    return div;
};

const appendSignatureFooter = () => {
    const footer = document.createElement('footer');
    footer.className = 'footer footer--signature';

    const wrapper = document.createElement('div');
    wrapper.className = 'footer-logo--wrapper';

    const clientLogo = document.createElement('img');
    clientLogo.className = 'footer-logo--artwork';
    clientLogo.style.marginLeft = '150px';

    const mainLogo = document.createElement('img');
    mainLogo.className = 'footer-logo';
    mainLogo.style.position = 'absolute';
    mainLogo.style.left = '50%';
    mainLogo.style.transform = 'translate(-50%, 0)';

    const spacer = document.createElement('div');
    wrapper.appendChild(clientLogo);
    wrapper.appendChild(mainLogo);
    wrapper.appendChild(spacer);

    const interpretedBy = document.createElement('p');
    interpretedBy.className = 'footer__interpreted-by';
    interpretedBy.textContent = 'Interpreted by____________________';

    footer.appendChild(wrapper);
    footer.appendChild(interpretedBy);
    document.body.appendChild(footer);
    return footer;
};

// ═══════════════════════════════════════════════════════════════════════════════
// downloadRasterPdf
// ═══════════════════════════════════════════════════════════════════════════════

describe('downloadRasterPdf', () => {
    it('powinien wywołać html2canvas na elemencie printContainer', async () => {
        appendWorkspace();

        await downloadRasterPdf();

        expect(mockHtml2canvas).toHaveBeenCalledWith(
            expect.any(Object),
            expect.objectContaining({
                backgroundColor: '#ffffff',
                useCORS: true,
            })
        );
    });

    it('powinien wygenerować PDF i wywołać pobieranie natywnego dyskowego linka typu BLOB', async () => {
        appendWorkspace();

        const windowUrlSpy = jest.spyOn(window.URL, 'createObjectURL').mockReturnValue('blob:test');
        const revokeUrlSpy = jest.spyOn(window.URL, 'revokeObjectURL').mockImplementation(() => {});
        const mockBlobOutput = new Blob();
        
        MockJsPDF.mockImplementation(() => ({
            addImage: mockAddImage,
            output: jest.fn().mockReturnValue(mockBlobOutput)
        }));

        const clickSpy = jest.spyOn(window.HTMLAnchorElement.prototype, 'click').mockImplementation(() => {});

        await downloadRasterPdf();

        expect(MockJsPDF).toHaveBeenCalledWith(
            expect.objectContaining({ format: 'a4', unit: 'mm', orientation: 'portrait' })
        );
        expect(mockAddImage).toHaveBeenCalledWith(
            expect.any(String), 'JPEG', expect.any(Number), expect.any(Number), expect.any(Number), expect.any(Number)
        );
        expect(windowUrlSpy).toHaveBeenCalledWith(mockBlobOutput);
        expect(clickSpy).toHaveBeenCalled();
        expect(revokeUrlSpy).toHaveBeenCalledWith('blob:test');

        windowUrlSpy.mockRestore();
        revokeUrlSpy.mockRestore();
        clickSpy.mockRestore();
    });


    it('powinna liczyć skalę tak, żeby bitmap mieścił się w 1240x1754 px (scaleX=scaleY)', async () => {
        // DOM element 620×877 → scaleX = 1240/620=2.0, rozszerzony test mock fallback daje totalHeight=977
        appendWorkspace(620, 877);

        await downloadRasterPdf();

        const callOptions = mockHtml2canvas.mock.calls[0][1];
        expect(callOptions.scale).toBeCloseTo(1.8, 1);
    });

    it('powinna użyć mniejszej skali (scaleX) gdy element jest szerszy niż A4-proporcja', async () => {
        // Szerokie zdjęcie: 620×400 (w teście)
        appendWorkspace(620, 400);

        await downloadRasterPdf();

        const callOptions = mockHtml2canvas.mock.calls[0][1];
        expect(callOptions.scale).toBeCloseTo(2.48, 1);
    });

    it('powinna użyć mniejszej skali (scaleY) gdy element jest wyższy niż A4-proporcja', async () => {
        // Wysokie zdjęcie: 300×877
        appendWorkspace(300, 877);

        await downloadRasterPdf();

        const callOptions = mockHtml2canvas.mock.calls[0][1];
        expect(callOptions.scale).toBeCloseTo(1.8, 1);
    });

    it('powinien logować błąd i nie generować PDF gdy element DOM nie istnieje', async () => {
        const consoleSpy = jest.spyOn(console, 'error').mockImplementation(() => {});

        // Element nie istnieje (wyczyśczone w beforeEach)
        await downloadRasterPdf();

        expect(consoleSpy).toHaveBeenCalledWith('Workspace element not found');
        expect(mockSave).not.toHaveBeenCalled();
        expect(mockAddImage).not.toHaveBeenCalled();

        consoleSpy.mockRestore();
    });

    it('powinien zachować podpis interpreted by i wycentrować logo w klonie print', async () => {
        appendWorkspace();
        appendSignatureFooter();

        await downloadRasterPdf();

        const signatureClone = capturedPrintContainer?.querySelector('.footer--signature') as HTMLElement;
        const logoWrapper = signatureClone?.querySelector('.footer-logo--wrapper') as HTMLElement;
        const logo = signatureClone?.querySelector('.footer-logo') as HTMLElement;
        const interpretedBy = signatureClone?.querySelector('.footer__interpreted-by') as HTMLElement;

        expect(signatureClone).toBeTruthy();
        expect(logoWrapper.style.justifyContent).toBe('center');
        expect(logoWrapper.style.flexDirection).toBe('column');
        expect(logo.style.position).toBe('static');
        expect(logo.style.margin).toBe('5px auto 34px auto');
        expect(interpretedBy?.textContent).toContain('Interpreted by');
        expect(interpretedBy.style.display).toBe('block');
        expect(interpretedBy.style.position).toBe('static');
        expect(interpretedBy.style.color).toBe('rgb(0, 0, 0)');
        expect(interpretedBy.style.textAlign).toBe('center');
    });

    it('powinien dodać fallback interpreted by gdy tekst nie jest gotowy przed capture', async () => {
        appendWorkspace();
        const footer = appendSignatureFooter();
        footer.querySelector('.footer__interpreted-by')?.remove();

        await downloadRasterPdf();

        const interpretedBy = capturedPrintContainer?.querySelector('.footer__interpreted-by') as HTMLElement;
        expect(interpretedBy?.textContent).toContain('Interpreted by');
        expect(interpretedBy.style.display).toBe('block');
        expect(interpretedBy.style.textAlign).toBe('center');
    });
});

// ═══════════════════════════════════════════════════════════════════════════════
// download (link click)
// ═══════════════════════════════════════════════════════════════════════════════

describe('download', () => {
    it('powinien wywołać click na linku z podanym href', () => {
        const clickSpy = jest.fn();
        const mockLink = {
            href: '',
            download: '',
            dispatchEvent: clickSpy,
        };
        jest.spyOn(document, 'createElement').mockReturnValueOnce(mockLink as any);

        download('https://example.com/file.pdf');

        expect(mockLink.href).toBe('https://example.com/file.pdf');
        expect(mockLink.download).toBe('file.pdf');
        expect(clickSpy).toHaveBeenCalledWith(expect.any(MouseEvent));

        jest.restoreAllMocks();
    });
});

// ═══════════════════════════════════════════════════════════════════════════════
// createPrintUrl
// ═══════════════════════════════════════════════════════════════════════════════

describe('createPrintUrl', () => {
    it('powinien generować URL z kolorami pasków', () => {
        const params = makeParams();
        const url = createPrintUrl(params as any, false, false);

        expect(url).toContain('_c1:FF0000');
        expect(url).toContain('_c2:00FF00');
        expect(url).toContain('_c3:0000FF');
    });

    it('powinien zapisywać ukryte kształty w URL, żeby QR odtwarzał ten sam obraz', () => {
        const params = makeParams();
        (params.shapes.shapes[2] as any).name = 'rhombus';
        (params.shapes.shapes[2] as any).tagName = 'hidden';
        const url = createPrintUrl(params as any, false, false);

        expect(url).toContain('_shape1-name:circle');
        expect(url).toContain('_shape2-name:square');
        expect(url).toContain('_shape3-name:hidden');
        expect(url).toContain('_shape3-rotate:0');
    });

    it('gdy print=true powinien kierować do printServiceUrl', () => {
        const params = makeParams();
        const url = createPrintUrl(params as any, true, false);

        expect(url).toContain('https://pdf.a-soft.pl/print/print?url=');
        expect(url).toContain('print=true');
    });

    it('gdy paramsOnly=true powinien zwracać tylko query string bez hosta', () => {
        const params = makeParams();
        const url = createPrintUrl(params as any, false, true);

        expect(url.startsWith('?print=')).toBe(true);
        expect(url).not.toContain('https://');
    });

    it('powinien użyć remoteAppUrl z konfiguracji, jeśli jest ustawiony', () => {
        const params = makeParams();
        const loadConfigSpy = jest.spyOn(ConfigUtils, 'loadConfig').mockReturnValue({
            environment: (window as any).config,
            application: { remoteAppUrl: 'https://live.cruz-diez-online.com/' } as any
        });
        
        const url = createPrintUrl(params as any, false, false);

        expect(url).toContain('https://live.cruz-diez-online.com/');
        expect(url).not.toContain('https://interactive.cruz-diez.com/');
        
        loadConfigSpy.mockRestore();
    });
});

// ═══════════════════════════════════════════════════════════════════════════════
// getShareUrl
// ═══════════════════════════════════════════════════════════════════════════════

describe('getShareUrl', () => {
    it('powinien zwracać pełny URL z origin + params', () => {
        const params = makeParams();
        const url = getShareUrl(params as any);

        expect(url).toMatch(/^https:\/\/interactive\.cruz-diez\.com/);
        expect(url).toContain('?print=');
    });
});

describe('getFittedPageLayout', () => {
    it('powinien dopasować render do strony A4 z zachowaniem proporcji jak PDF', () => {
        const layout = getFittedPageLayout(1602, 1550, 1240, 1754);

        expect(layout.width).toBe(1240);
        expect(layout.height).toBeCloseTo(1199.75, 2);
        expect(layout.offsetX).toBe(0);
        expect(layout.offsetY).toBeCloseTo(277.12, 2);
    });
});

describe('getA4RasterCaptureScale', () => {
    it('powinien liczyć tę samą skalę rasteryzacji dla PDF i obrazu QR', () => {
        expect(getA4RasterCaptureScale(800, 707)).toBeCloseTo(1.75, 2);
        expect(getA4RasterCaptureScale(620, 977)).toBeCloseTo(1.8, 1);
    });
});

describe('generateArtworkDataUrl', () => {
    it('powinien rasteryzować obraz QR tą samą skalą A4 co PDF', async () => {
        appendWorkspace(800, 707);
        const getContextSpy = jest.spyOn(HTMLCanvasElement.prototype, 'getContext').mockReturnValue(null);

        await generateArtworkDataUrl();

        const callOptions = mockHtml2canvas.mock.calls[0][1];
        expect(callOptions.scale).toBeCloseTo(getA4RasterCaptureScale(800, 807), 2);

        getContextSpy.mockRestore();
    });

    it('powinien wyłączyć transform body podczas rasteryzacji QR i przywrócić go po renderze', async () => {
        appendWorkspace(800, 707);
        const getContextSpy = jest.spyOn(HTMLCanvasElement.prototype, 'getContext').mockReturnValue(null);
        document.body.style.transform = 'scale(0.9)';
        let transformDuringCapture = '';

        mockHtml2canvas.mockImplementationOnce(() => {
            transformDuringCapture = document.body.style.transform;
            return Promise.resolve(mockCanvas);
        });

        await generateArtworkDataUrl();

        expect(transformDuringCapture).toBe('none');
        expect(document.body.style.transform).toBe('scale(0.9)');

        document.body.style.transform = '';
        getContextSpy.mockRestore();
    });
});

describe('drawTextOnCanvas', () => {
    it('rysuje podpis interpreted by bezpośrednio na canvasie dla Safari/iOS', () => {
        const save = jest.fn();
        const restore = jest.fn();
        const fillText = jest.fn();
        const context = {
            save,
            restore,
            fillText,
            fillStyle: '',
            font: '',
            textAlign: '',
            textBaseline: '',
        };
        const canvas = {
            getContext: jest.fn().mockReturnValue(context),
        } as any;

        drawTextOnCanvas(canvas, 'Interpreted by____________________', 400, 750, 22);

        expect(context.fillStyle).toBe('#000000');
        expect(context.font).toBe('22px Arial, sans-serif');
        expect(context.textAlign).toBe('center');
        expect(fillText).toHaveBeenCalledWith('Interpreted by____________________', 400, 750);
        expect(save).toHaveBeenCalled();
        expect(restore).toHaveBeenCalled();
    });
});

describe('drawSignatureTextOnA4Canvas', () => {
    it('rysuje podpis w stałej widocznej pozycji finalnej strony A4', () => {
        const fillText = jest.fn();
        const context = {
            save: jest.fn(),
            restore: jest.fn(),
            fillText,
            fillStyle: '',
            font: '',
            textAlign: '',
            textBaseline: '',
        };
        const canvas = {
            width: 1754,
            height: 1240,
            getContext: jest.fn().mockReturnValue(context),
        } as any;

        drawSignatureTextOnA4Canvas(canvas, 'Interpreted by____________________');

        expect(fillText).toHaveBeenCalledWith('Interpreted by____________________', 877, 1220);
        expect(context.font).toBe('22px Arial, sans-serif');
        expect(context.textAlign).toBe('center');
    });
});
